<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Create Admin Account</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        New admin will have full access to manage the platform
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-shield-account</v-icon>
                      Admin Registration
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.push('/users')"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Users
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-4 py-3">
              <v-alert v-if="errorMessage" type="error" dense class="mb-4">
                {{ errorMessage }}
              </v-alert>

              <v-form ref="form" lazy-validation>
                <!-- Personal Information -->
                <div class="form-section mb-3">
                  <h3 class="section-title mb-2">
                    <v-icon color="primary" class="mr-2" small>mdi-account</v-icon>
                    Personal Information
                  </h3>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.first_name"
                        label="First Name *"
                        placeholder="Enter first name"
                        outlined
                        class="professional-field"
                        hide-details="auto"
                        :error-messages="errors.first_name"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey" small>mdi-account-outline</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.last_name"
                        label="Last Name *"
                        placeholder="Enter last name"
                        outlined
                        class="professional-field"
                        hide-details="auto"
                        :error-messages="errors.last_name"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey" small>mdi-account-outline</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.email"
                        label="Email Address *"
                        placeholder="Enter email address"
                        type="email"
                        outlined
                        class="professional-field"
                        hide-details="auto"
                        :error-messages="errors.email"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey" small>mdi-email-outline</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.phone"
                        label="Phone Number"
                        placeholder="10-digit mobile number"
                        outlined
                        maxlength="10"
                        class="professional-field"
                        hide-details="auto"
                        :error-messages="errors.phone"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey" small>mdi-phone-outline</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Security -->
                <div class="form-section">
                  <h3 class="section-title mb-2">
                    <v-icon color="primary" class="mr-2" small>mdi-lock</v-icon>
                    Security
                  </h3>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.password"
                        label="Password *"
                        placeholder="Minimum 6 characters"
                        :type="showPassword ? 'text' : 'password'"
                        :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                        @click:append="showPassword = !showPassword"
                        outlined
                        class="professional-field"
                        hide-details="auto"
                        :error-messages="errors.password"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey" small>mdi-lock-outline</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.password_confirmation"
                        label="Confirm Password *"
                        placeholder="Re-enter password"
                        :type="showConfirm ? 'text' : 'password'"
                        :append-icon="showConfirm ? 'mdi-eye-off' : 'mdi-eye'"
                        @click:append="showConfirm = !showConfirm"
                        outlined
                        class="professional-field"
                        hide-details="auto"
                        :error-messages="errors.password_confirmation"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey" small>mdi-lock-check-outline</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Actions -->
            <v-card-actions class="px-4 py-3">
              <v-spacer></v-spacer>
              <v-btn
                color="grey darken-1"
                text
                @click="$router.push('/users')"
                class="back-button"
              >
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                :loading="loading"
                @click="submit"
              >
                <v-icon left small>mdi-check</v-icon>
                Create Admin
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'CreateAdminPage',
  data() {
    return {
      loading: false,
      showPassword: false,
      showConfirm: false,
      errorMessage: '',
      errors: {},
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        password_confirmation: '',
      },
    };
  },
  methods: {
    async submit() {
      this.errors = {};
      this.errorMessage = '';
      this.loading = true;
      try {
        await axios.post('/users/create_admin', this.form);
        this.$toast.success('Admin account created successfully.');
        this.$router.push('/users');
      } catch (e) {
        const data = e.response?.data;
        if (data?.errors) {
          this.errors = data.errors;
        } else {
          this.errorMessage = data?.message || 'Failed to create admin account.';
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
